import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Theme choice: the OLED black that is the brand, a light theme, or whatever
 * the device says. Only the resolved value reaches the stylesheet, as
 * `data-theme` on the root, so tokens.css never needs a media query; the
 * browser chrome follows through the theme-color meta.
 */

export const THEMES = ['system', 'dark', 'light'] as const
export type Theme = (typeof THEMES)[number]
export type ResolvedTheme = 'dark' | 'light'

const STORAGE_KEY = 'theme'
const THEME_COLOR: Record<ResolvedTheme, string> = { dark: '#000000', light: '#f4f7f6' }

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isTheme(stored)) return stored
  } catch {
    // Private mode and blocked site data both throw here.
  }
  return 'system'
}

function systemPrefersLight(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: light)').matches
}

export function resolveTheme(theme: Theme, prefersLight: boolean = systemPrefersLight()): ResolvedTheme {
  if (theme === 'system') return prefersLight ? 'light' : 'dark'
  return theme
}

/** Writes the resolved theme onto the document; safe to call before React mounts. */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement
  if (resolved === 'light') root.dataset['theme'] = 'light'
  else delete root.dataset['theme']
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta !== null) meta.setAttribute('content', THEME_COLOR[resolved])
}

interface ThemeValue {
  theme: Theme
  resolved: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)
  const [prefersLight, setPrefersLight] = useState(systemPrefersLight)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (event: MediaQueryListEvent): void => setPrefersLight(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolved = resolveTheme(theme, prefersLight)
  useEffect(() => {
    applyTheme(resolved)
  }, [resolved])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Persisting the choice is a convenience, never a requirement.
    }
  }, [])

  const value = useMemo<ThemeValue>(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>')
  return value
}
